/**
 * TanStack Query hooks for Bank Accounts
 */

import type {
  BankAccountPublic,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@garden-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bankAccountsApi } from '../services/organizationSettingsApi';

export const bankAccountQueryKeys = {
  all: ['bankAccounts'] as const,
  list: (includeInactive?: boolean) =>
    [...bankAccountQueryKeys.all, 'list', { includeInactive }] as const,
  detail: (id: string) => [...bankAccountQueryKeys.all, 'detail', id] as const,
};

/**
 * Fetch all bank accounts
 */
export function useBankAccounts(includeInactive = false) {
  return useQuery({
    queryKey: bankAccountQueryKeys.list(includeInactive),
    queryFn: async () => {
      const response = await bankAccountsApi.list(includeInactive);
      return response.data as BankAccountPublic[];
    },
  });
}

/**
 * Fetch a single bank account
 */
export function useBankAccount(id: string) {
  return useQuery({
    queryKey: bankAccountQueryKeys.detail(id),
    queryFn: async () => {
      const response = await bankAccountsApi.get(id);
      return response.data as BankAccountPublic;
    },
    enabled: !!id,
  });
}

/**
 * Create a new bank account
 */
export function useCreateBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBankAccountRequest) => {
      const response = await bankAccountsApi.create(data);
      return response.data as BankAccountPublic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bankAccountQueryKeys.all,
      });
    },
  });
}

/**
 * Update a bank account
 */
export function useUpdateBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBankAccountRequest }) => {
      const response = await bankAccountsApi.update(id, data);
      return response.data as BankAccountPublic;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: bankAccountQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: bankAccountQueryKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Set a bank account as default
 */
export function useSetDefaultBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await bankAccountsApi.setDefault(id);
      return response.data as BankAccountPublic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bankAccountQueryKeys.all,
      });
    },
  });
}

/**
 * Delete a bank account
 */
export function useDeleteBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await bankAccountsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bankAccountQueryKeys.all,
      });
    },
  });
}
